import { runtimeConfig } from '@/config/runtime-config'
import { createApiClient } from '@/lib/api/http-client'
import { getStoredToken } from '@/lib/auth/token-storage'

interface SuccessEnvelope<T> {
  code: 'success'
  data: T
}

interface MessageEnvelope {
  code: 'success'
  message: string
}

export type MCPConnectionHealthStatus =
  | 'blocked'
  | 'healthy'
  | 'invalid_response'
  | 'unknown'
  | 'unreachable'

export interface MCPConnectionHealth {
  error_code: string | null
  last_checked_at: string | null
  last_healthy_at: string | null
  status: MCPConnectionHealthStatus
}

export interface MCPDiscoverySnapshot {
  capabilities: Record<string, unknown>
  connection_config_revision: number
  created_at: string
  discovery_generation: number
  endpoint_policy_revision: string
  id: string
  protocol_version: string
  server_name: string | null
  server_version: string | null
  snapshot_revision: string
  tool_set_hash: string
}

export type MCPOAuthKind = 'external' | 'hub' | 'none'
export type MCPExternalOAuthRegistrationMode = 'automatic' | 'pre_registered'
export type MCPTokenEndpointAuthMethod =
  | 'auto'
  | 'client_secret_basic'
  | 'client_secret_post'
  | 'none'
export type MCPExternalOAuthStatus =
  | 'authorized'
  | 'authorizing'
  | 'disconnected'
  | 'error'
  | 'reconnect_required'

export interface MCPExternalOAuthInfo {
  authorization_server_issuer: string | null
  client_id: string | null
  client_secret_configured: boolean
  issuer_binding_status: 'pending' | 'verified' | null
  registration_mode: MCPExternalOAuthRegistrationMode
  status: MCPExternalOAuthStatus
  token_endpoint_auth_method: MCPTokenEndpointAuthMethod | null
}

export interface MCPExternalOAuthRegistrationWrite {
  authorization_server_issuer?: string
  client_id?: string
  client_secret?: string
  mode: MCPExternalOAuthRegistrationMode
  token_endpoint_auth_method?: MCPTokenEndpointAuthMethod
}

export interface MCPExternalOAuthConfiguration {
  callback_uri: string
  cimd_available: boolean
  client_metadata_document_uri: string
  registration_modes: MCPExternalOAuthRegistrationMode[]
  token_endpoint_auth_methods: MCPTokenEndpointAuthMethod[]
}

export interface MCPConnection {
  auth_config_revision: number
  config_revision: number
  created_at: string
  credential_configured: boolean
  current_snapshot: MCPDiscoverySnapshot | null
  current_snapshot_id: string | null
  discovery_generation: number
  display_name: string
  enabled: boolean
  endpoint_policy_revision: string
  endpoint_uri: string
  external_oauth: MCPExternalOAuthInfo | null
  health: MCPConnectionHealth
  id: string
  oauth_kind: MCPOAuthKind
  oauth_scopes: string[]
  request_headers: MCPConnectionRequestHeader[]
  resource_uri: string | null
  updated_at: string
}

export interface MCPConnectionRequestHeader {
  id: string
  name: string
  position: number
  value_configured: boolean
}

export interface MCPConnectionRequestHeaderWrite {
  id?: string
  name: string
  value?: string
}

export interface MCPConnectionCreatePayload {
  credential_reference?: string
  display_name: string
  enabled?: boolean
  endpoint_uri: string
  external_oauth_registration?: MCPExternalOAuthRegistrationWrite
  oauth_kind?: MCPOAuthKind
  oauth_scopes?: string[]
  request_headers?: MCPConnectionRequestHeaderWrite[]
  resource_uri?: string
}

export interface MCPConnectionUpdatePayload {
  credential_reference?: string | null
  display_name?: string
  enabled?: boolean
  endpoint_uri?: string
  expected_config_revision: number
  external_oauth_registration?: MCPExternalOAuthRegistrationWrite
  oauth_kind?: MCPOAuthKind
  oauth_scopes?: string[]
  request_headers?: MCPConnectionRequestHeaderWrite[]
  resource_uri?: string | null
}

export interface MCPToolDefinition {
  annotations: Record<string, unknown>
  definition_revision: string
  description: string | null
  display_name: string | null
  execution: Record<string, unknown> | null
  execution_policy_revision: string
  id: string
  input_schema: Record<string, unknown>
  output_schema: Record<string, unknown> | null
  remote_name: string
  schema_hash: string
  source_reference: string
}

export interface MCPToolDefinitionList {
  config_revision: number
  connection_id: string
  items: MCPToolDefinition[]
  snapshot_id: string | null
  snapshot_revision: string | null
}

export interface MCPRefreshResult {
  connection: MCPConnection
  diff: {
    added: string[]
    changed: string[]
    removed: string[]
  }
}

export interface MCPExternalOAuthAuthorizeResult {
  authorization_url: string
  connection: MCPConnection
  flow_id: string
}

export type MCPExternalOAuthFlowStatus = 'authorized' | 'failed' | 'pending' | 'processing'

export interface MCPExternalOAuthFlowResult {
  error_code: string | null
  flow_id: string
  status: MCPExternalOAuthFlowStatus
}

export interface MCPPendingExternalOAuth {
  connectionId: string
  flowId: string
  startedAt: number
}

export interface AgentToolConnectionSummary {
  display_name: string
  enabled: boolean
  health_status: string
  id: string
}

export type AgentToolUnavailableReason = 'connection_disabled' | 'tool_missing' | null

export interface AgentToolOption {
  annotations: Record<string, unknown>
  available: boolean
  bound_definition_id: string | null
  connection: AgentToolConnectionSummary | null
  definition_id: string | null
  definition_revision: string | null
  description: string
  display_name: string
  enabled: boolean
  execution: Record<string, unknown> | null
  execution_available: boolean
  execution_policy_revision: string | null
  input_schema: Record<string, unknown> | null
  name: string
  output_schema: Record<string, unknown> | null
  remote_name: string | null
  review_required: boolean
  schema_hash: string
  selected: boolean
  source: 'builtin' | 'mcp'
  source_reference: string
  unavailable_reason: AgentToolUnavailableReason
}

export interface AgentToolBindings {
  agent_id: string
  agent_type: 'core_agent' | 'self_agent'
  selection_revision: string
  tools: AgentToolOption[]
}

export interface AgentToolCatalog {
  tools: AgentToolOption[]
}

export interface AgentToolBindingWrite {
  definition_id?: string
  source: 'builtin' | 'mcp'
  source_reference?: string
  tool_name: string
}

export interface AgentToolBindingsUpdatePayload {
  bindings: AgentToolBindingWrite[]
  expected_revision: string
}

export type AgentToolSelectionErrorCode =
  | 'definition_missing'
  | 'review_required'
  | 'tool_name_conflict'

export class AgentToolSelectionError extends Error {
  constructor(
    public readonly code: AgentToolSelectionErrorCode,
    public readonly sourceReference: string,
    public readonly toolName?: string,
  ) {
    super(code)
    this.name = 'AgentToolSelectionError'
  }
}

const apiClient = createApiClient({
  baseUrl: runtimeConfig.apiBaseUrl,
  getToken: () => getStoredToken('admin'),
  portal: 'admin',
})

function connectionPath(connectionId: string) {
  return `/admin/mcp-connections/${encodeURIComponent(connectionId)}`
}

const pendingExternalOAuthConnectionKey = 'admin_mcp_external_oauth_connection_id'

export async function listMCPConnections() {
  const response =
    await apiClient.request<SuccessEnvelope<{ items: MCPConnection[] }>>('/admin/mcp-connections')
  return response.data.items
}

export async function getMCPConnection(connectionId: string) {
  const response = await apiClient.request<SuccessEnvelope<MCPConnection>>(
    connectionPath(connectionId),
  )
  return response.data
}

export async function getMCPExternalOAuthConfiguration() {
  const response = await apiClient.request<SuccessEnvelope<MCPExternalOAuthConfiguration>>(
    '/admin/mcp-connections/oauth/configuration',
  )
  return response.data
}

export async function createMCPConnection(payload: MCPConnectionCreatePayload) {
  const response = await apiClient.request<SuccessEnvelope<MCPConnection>>(
    '/admin/mcp-connections',
    { body: JSON.stringify(payload), method: 'POST' },
  )
  return response.data
}

export async function updateMCPConnection(
  connectionId: string,
  payload: MCPConnectionUpdatePayload,
) {
  const response = await apiClient.request<SuccessEnvelope<MCPConnection>>(
    connectionPath(connectionId),
    { body: JSON.stringify(payload), method: 'PUT' },
  )
  return response.data
}

export async function deleteMCPConnection(connectionId: string, expectedConfigRevision: number) {
  const query = new URLSearchParams({
    expected_config_revision: String(expectedConfigRevision),
  })
  await apiClient.request<MessageEnvelope>(`${connectionPath(connectionId)}?${query.toString()}`, {
    method: 'DELETE',
  })
}

export async function refreshMCPConnection(connectionId: string, expectedConfigRevision: number) {
  const response = await apiClient.request<SuccessEnvelope<MCPRefreshResult>>(
    `${connectionPath(connectionId)}/refresh`,
    {
      body: JSON.stringify({ expected_config_revision: expectedConfigRevision }),
      method: 'POST',
    },
  )
  return response.data
}

export async function authorizeMCPExternalOAuth(
  connectionId: string,
  expectedConfigRevision: number,
) {
  const response = await apiClient.request<SuccessEnvelope<MCPExternalOAuthAuthorizeResult>>(
    `${connectionPath(connectionId)}/oauth/authorize`,
    {
      body: JSON.stringify({ expected_config_revision: expectedConfigRevision }),
      method: 'POST',
    },
  )
  return response.data
}

export async function disconnectMCPExternalOAuth(
  connectionId: string,
  expectedConfigRevision: number,
) {
  const response = await apiClient.request<SuccessEnvelope<MCPConnection>>(
    `${connectionPath(connectionId)}/oauth/disconnect`,
    {
      body: JSON.stringify({ expected_config_revision: expectedConfigRevision }),
      method: 'POST',
    },
  )
  return response.data
}

export async function getMCPExternalOAuthFlow(connectionId: string, flowId: string) {
  const response = await apiClient.request<SuccessEnvelope<MCPExternalOAuthFlowResult>>(
    `${connectionPath(connectionId)}/oauth/flows/${encodeURIComponent(flowId)}`,
  )
  return response.data
}

export function rememberPendingMCPExternalOAuth(connectionId: string, flowId: string) {
  window.sessionStorage.setItem(
    pendingExternalOAuthConnectionKey,
    JSON.stringify({
      connectionId,
      flowId,
      startedAt: Date.now(),
    } satisfies MCPPendingExternalOAuth),
  )
}

export function readPendingMCPExternalOAuth() {
  const raw = window.sessionStorage.getItem(pendingExternalOAuthConnectionKey)
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'connectionId' in parsed &&
      typeof parsed.connectionId === 'string' &&
      'flowId' in parsed &&
      typeof parsed.flowId === 'string' &&
      'startedAt' in parsed &&
      typeof parsed.startedAt === 'number'
    ) {
      return parsed as MCPPendingExternalOAuth
    }
  } catch {
    // 旧版本只保存 connection ID，无法安全关联某一次 flow，直接清理。
  }
  clearPendingMCPExternalOAuth()
  return null
}

export function clearPendingMCPExternalOAuth() {
  window.sessionStorage.removeItem(pendingExternalOAuthConnectionKey)
}

export function openMCPExternalOAuthPopup(
  connectionId: string,
  flowId: string,
  authorizationUrl: string,
) {
  const url = new URL(authorizationUrl)
  if (url.protocol !== 'https:') {
    throw new Error('MCP OAuth authorization URL must use HTTPS')
  }

  rememberPendingMCPExternalOAuth(connectionId, flowId)
  const width = 600
  const height = 680
  const left = window.screenX + (window.outerWidth - width) / 2
  const top = window.screenY + (window.outerHeight - height) / 2
  const popup = window.open(
    url.href,
    'MCP OAuth',
    [
      `width=${String(width)}`,
      `height=${String(height)}`,
      `left=${String(left)}`,
      `top=${String(top)}`,
      'scrollbars=yes',
    ].join(','),
  )
  if (!popup) {
    window.location.assign(url.href)
    return null
  }

  // 授权页不应保留反向控制管理页的 opener；完成状态由后端 flow 轮询恢复。
  try {
    popup.opener = null
  } catch {
    // 个别浏览器会阻止跨 origin WindowProxy 写入；flow 轮询仍不依赖 opener。
  }

  return popup
}

export async function listMCPConnectionTools(connectionId: string) {
  const response = await apiClient.request<SuccessEnvelope<MCPToolDefinitionList>>(
    `${connectionPath(connectionId)}/tools`,
  )
  return response.data
}

export async function getCoreAgentToolBindings() {
  const response = await apiClient.request<SuccessEnvelope<AgentToolBindings>>(
    '/admin/agent-tools/core-agent/bindings',
  )
  return response.data
}

export async function getAgentToolCatalog() {
  const response = await apiClient.request<SuccessEnvelope<AgentToolCatalog>>(
    '/admin/agent-tools/catalog',
  )
  return response.data
}

export async function updateCoreAgentToolBindings(payload: AgentToolBindingsUpdatePayload) {
  const response = await apiClient.request<SuccessEnvelope<AgentToolBindings>>(
    '/admin/agent-tools/core-agent/bindings',
    { body: JSON.stringify(payload), method: 'PUT' },
  )
  return response.data
}

export async function getSelfAgentToolBindings(appId: string) {
  const response = await apiClient.request<SuccessEnvelope<AgentToolBindings>>(
    `/admin/agent-tools/self-agent/${encodeURIComponent(appId)}/bindings`,
  )
  return response.data
}

export async function updateSelfAgentToolBindings(
  appId: string,
  payload: AgentToolBindingsUpdatePayload,
) {
  const response = await apiClient.request<SuccessEnvelope<AgentToolBindings>>(
    `/admin/agent-tools/self-agent/${encodeURIComponent(appId)}/bindings`,
    { body: JSON.stringify(payload), method: 'PUT' },
  )
  return response.data
}

export function agentToolIdentityKey(tool: Pick<AgentToolOption, 'source' | 'source_reference'>) {
  return `${tool.source}:${tool.source_reference}`
}

export function currentConcreteAgentToolIdentities(tools: AgentToolOption[]) {
  return tools
    .filter((tool) => tool.source === 'builtin' || Boolean(tool.definition_id))
    .map((tool) => agentToolIdentityKey(tool))
}

/**
 * 把当前页面的选择展开成后端要求的具体 Binding 集合。
 * MCP 只提交 current definition ID 与后端投影的 canonical Tool name；管理员不编辑 namespace 或 alias。
 * source reference 由后端从 immutable definition 解析。
 */
export function buildAgentToolBindingsPayload(
  state: AgentToolBindings,
  selectedIdentities: ReadonlySet<string>,
  confirmedReviewIdentities: ReadonlySet<string> = new Set(),
): AgentToolBindingsUpdatePayload {
  const bindings: AgentToolBindingWrite[] = []
  const seenToolNames = new Set<string>()

  for (const tool of state.tools) {
    const identity = agentToolIdentityKey(tool)
    if (!selectedIdentities.has(identity)) {
      continue
    }

    if (tool.review_required && !confirmedReviewIdentities.has(identity)) {
      throw new AgentToolSelectionError('review_required', tool.source_reference)
    }

    if (seenToolNames.has(tool.name)) {
      throw new AgentToolSelectionError('tool_name_conflict', tool.source_reference, tool.name)
    }
    seenToolNames.add(tool.name)

    if (tool.source === 'builtin') {
      bindings.push({
        source: 'builtin',
        source_reference: tool.source_reference,
        tool_name: tool.name,
      })
      continue
    }

    if (!tool.definition_id) {
      throw new AgentToolSelectionError('definition_missing', tool.source_reference)
    }
    bindings.push({ definition_id: tool.definition_id, source: 'mcp', tool_name: tool.name })
  }

  if (bindings.length !== selectedIdentities.size) {
    throw new AgentToolSelectionError('definition_missing', '')
  }

  return {
    bindings,
    expected_revision: state.selection_revision,
  }
}
